import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { getSignalRUrl } from '../constants/api-endpoints';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {

  private hubConnection!: signalR.HubConnection;
  private notificationSubject = new Subject<any>();
  private currentUserId: string | null = null;
  private currentSessionId: string | null = null;
  private reconnectTimer: any = null;
  
  // Observable for components to subscribe to
  notification$ = this.notificationSubject.asObservable();

  constructor(
    private ngZone: NgZone
  ) {}

  async startConnection(userId: string, accessTokenFactory: () => string, sessionId: string): Promise<void> {
    const baseUrl = getSignalRUrl();
    if (!baseUrl || !userId || userId.includes('null') || userId.includes('undefined')) {
      return;
    }

    if (this.currentUserId === userId && this.currentSessionId === sessionId &&
        this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      return;
    }

    this.currentUserId = userId;
    this.currentSessionId = sessionId;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Stop existing connection if any
    if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      try {
        await this.hubConnection.stop();
      } catch (e) {
        // ignore error while stopping
      }
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/hubs/notifications?userId=${encodeURIComponent(userId)}`, {
        accessTokenFactory
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Register notification handler BEFORE starting connection
    this.registerNotificationHandler();

    this.hubConnection.onclose((err: Error | undefined) => {
      if (err) console.warn('SignalR closed with error:', err);
    });

    this.hubConnection.onreconnecting((err: Error | undefined) => {
      if (err) console.warn('SignalR reconnecting...', err);
    });

    this.hubConnection.onreconnected((id: string | undefined) => {
      console.log('SignalR reconnected successfully. Hub ConnectionId:', id);
    });

    try {
      await this.hubConnection.start();
      console.log(`SignalR connected successfully for user/location: ${userId}`);
    } catch (err: any) {
      console.warn('SignalR start connection error:', err);
      // Retry once after 5 seconds if initial connect failed
      this.reconnectTimer = setTimeout(() => {
        if (this.hubConnection?.state === signalR.HubConnectionState.Disconnected &&
            this.currentUserId === userId && this.currentSessionId === sessionId) {
          this.hubConnection.start().catch((retryErr: any) => {
            console.warn('SignalR retry connection failed:', retryErr);
          });
        }
      }, 5000);
    }
  }

  private registerNotificationHandler(): void {
    if (!this.hubConnection) return;
    this.hubConnection.off('ReceiveNotification');
    this.hubConnection.on('ReceiveNotification', (data: any) => {
      this.ngZone.run(() => {
        this.notificationSubject.next(data);
      });
    });
  }

  onNotification(callback: (data: any) => void): void {
    if (this.hubConnection) {
      this.hubConnection.on('ReceiveNotification', (data: any) => {
        this.ngZone.run(() => {
          callback(data);
        });
      });
    }
  }

  stopConnection(): void {
    this.currentUserId = null;
    this.currentSessionId = null;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      this.hubConnection.stop()
        .then(() => {})
        .catch((err: any) => {});
    }
  }

  isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }
}


